<template>
  <div class="flex flex-col">
    <!-- Month navigation -->
    <div class="flex items-center justify-between mb-2">
      <button
        type="button"
        class="w-8 h-8 rounded-lg border border-surface-200 flex items-center justify-center"
        :aria-label="prevMonthLabel"
        @click="navigateMonth(-1)"
      >
        <i class="pi pi-chevron-left text-xs text-surface-600 rtl:rotate-180" />
      </button>
      <span class="text-sm font-semibold">{{ months[viewMonth.m] }} {{ viewMonth.y }}</span>
      <button
        type="button"
        class="w-8 h-8 rounded-lg border border-surface-200 flex items-center justify-center"
        :aria-label="nextMonthLabel"
        @click="navigateMonth(1)"
      >
        <i class="pi pi-chevron-right text-xs text-surface-600 rtl:rotate-180" />
      </button>
    </div>

    <!-- Weekday headers (Sunday first) -->
    <div class="grid grid-cols-7 gap-0.5 text-center mb-0.5">
      <span
        v-for="weekday in weekdays"
        :key="weekday"
        class="text-[10.5px] font-semibold text-surface-400 py-1"
      >{{ weekday }}</span>
    </div>

    <!-- Day grid -->
    <div class="grid grid-cols-7 gap-0.5">
      <span v-for="i in firstDayOffset" :key="`blank-${i}`" />
      <button
        v-for="day in daysInMonth"
        :key="day"
        type="button"
        :disabled="isDisabled(day)"
        class="h-[34px] flex items-center justify-center text-[12.5px] box-border border-[1.5px] transition-colors"
        :class="dayClass(day)"
        :style="dayStyle(day)"
        @click="tapDay(day)"
      >{{ day }}</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

/**
 * Inline month grid for picking a single date or a start–end range. Shared by the
 * dashboard's date bottom-sheet and the booking site, so it has no drawer/confirm logic
 * of its own — every tap emits the new value.
 *
 * v-model: a Date (single) or `[start, end]` (range; `end` is null until the second tap).
 * By default it uses the app's `primary` theme colors; pass `accentColor` (any CSS color)
 * to follow a business's own design on the booking site.
 */
const props = withDefaults(defineProps<{
  mode?: 'single' | 'range';
  modelValue?: Date | Array<Date | null> | null;
  minDate?: Date;
  maxDate?: Date;
  /** 12 month names, January first. */
  monthNames?: string[];
  /** 7 short weekday names, Sunday first. */
  weekdayNames?: string[];
  accentColor?: string;
  accentTextColor?: string;
  prevMonthLabel?: string;
  nextMonthLabel?: string;
}>(), {
  mode: 'single',
  prevMonthLabel: 'חודש קודם',
  nextMonthLabel: 'חודש הבא',
});

const emit = defineEmits<{ 'update:modelValue': [value: Date | Array<Date | null>] }>();

interface DayKey { d: number; m: number; y: number }

const HE_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const HE_WEEKDAYS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

const months = computed(() => props.monthNames?.length === 12 ? props.monthNames : HE_MONTHS);
const weekdays = computed(() => props.weekdayNames?.length === 7 ? props.weekdayNames : HE_WEEKDAYS);

const rangeMode = computed(() => props.mode === 'range');

const today = new Date();
const validDate = (d?: Date | null): Date | null => (d instanceof Date && !isNaN(d.getTime()) ? d : null);
const toKey = (d: Date): DayKey => ({ d: d.getDate(), m: d.getMonth(), y: d.getFullYear() });
const keyToDate = (k: DayKey) => new Date(k.y, k.m, k.d);
const keyNum = (k: DayKey) => k.y * 10000 + k.m * 100 + k.d;

const selected = computed<DayKey | null>(() => {
  if (rangeMode.value) return null;
  const d = validDate(props.modelValue as Date | null);
  return d ? toKey(d) : null;
});
const rangeStart = computed<DayKey | null>(() => {
  const d = Array.isArray(props.modelValue) ? validDate(props.modelValue[0]) : null;
  return d ? toKey(d) : null;
});
const rangeEnd = computed<DayKey | null>(() => {
  const d = Array.isArray(props.modelValue) ? validDate(props.modelValue[1]) : null;
  return d ? toKey(d) : null;
});

// Open on the selected date, else on the first selectable month.
const initial = (rangeMode.value ? rangeStart.value : selected.value)
  ?? toKey(validDate(props.minDate) && props.minDate! > today ? props.minDate! : today);
const viewMonth = ref({ y: initial.y, m: initial.m });

// Follow a value set from outside (e.g. a "today" button) to its month.
watch(() => (rangeMode.value ? rangeStart.value : selected.value), (k) => {
  if (k && (k.m !== viewMonth.value.m || k.y !== viewMonth.value.y)) viewMonth.value = { y: k.y, m: k.m };
});

const firstDayOffset = computed(() => new Date(viewMonth.value.y, viewMonth.value.m, 1).getDay());
const daysInMonth = computed(() => new Date(viewMonth.value.y, viewMonth.value.m + 1, 0).getDate());

const navigateMonth = (dir: number) => {
  const next = viewMonth.value.m + dir;
  viewMonth.value = { y: viewMonth.value.y + Math.floor(next / 12), m: ((next % 12) + 12) % 12 };
};

const dayKey = (day: number): DayKey => ({ d: day, m: viewMonth.value.m, y: viewMonth.value.y });
const sameDay = (k: DayKey | null, day: number) =>
  !!k && k.d === day && k.m === viewMonth.value.m && k.y === viewMonth.value.y;

const isSelected = (day: number) => rangeMode.value
  ? sameDay(rangeStart.value, day) || sameDay(rangeEnd.value, day)
  : sameDay(selected.value, day);

const inRange = (day: number) => {
  if (!rangeMode.value || !rangeStart.value || !rangeEnd.value) return false;
  const k = keyNum(dayKey(day));
  return k > keyNum(rangeStart.value) && k < keyNum(rangeEnd.value);
};

const isToday = (day: number) =>
  today.getDate() === day && today.getMonth() === viewMonth.value.m && today.getFullYear() === viewMonth.value.y;

// Day is outside the allowed [minDate, maxDate] window (date-only comparison).
const isDisabled = (day: number) => {
  const n = keyNum(dayKey(day));
  if (validDate(props.minDate) && n < keyNum(toKey(props.minDate!))) return true;
  if (validDate(props.maxDate) && n > keyNum(toKey(props.maxDate!))) return true;
  return false;
};

const dayClass = (day: number) => {
  const shape = inRange(day) ? 'rounded-none' : 'rounded-[10px]';
  if (isDisabled(day)) return [shape, 'text-surface-300 border-transparent cursor-not-allowed'];
  if (isSelected(day)) return [shape, 'font-bold border-transparent', props.accentColor ? '' : 'bg-primary text-white'];
  if (inRange(day)) return [shape, 'text-surface-800 border-transparent', props.accentColor ? '' : 'bg-primary-100'];
  if (isToday(day)) return [shape, 'text-surface-800', props.accentColor ? '' : 'border-primary-300'];
  return [shape, 'text-surface-800 border-transparent'];
};

const dayStyle = (day: number) => {
  const accent = props.accentColor;
  if (!accent || isDisabled(day)) return undefined;
  if (isSelected(day)) return { backgroundColor: accent, color: props.accentTextColor || '#fff' };
  if (inRange(day)) return { backgroundColor: `color-mix(in srgb, ${accent} 18%, transparent)` };
  if (isToday(day)) return { borderColor: `color-mix(in srgb, ${accent} 55%, transparent)` };
  return undefined;
};

const tapDay = (day: number) => {
  if (isDisabled(day)) return;
  const k = dayKey(day);
  if (!rangeMode.value) {
    emit('update:modelValue', keyToDate(k));
    return;
  }
  // first tap sets the start; second sets the end (swapped when tapped before the start);
  // a third tap starts a fresh range
  const start = rangeStart.value;
  if (!start || rangeEnd.value) {
    emit('update:modelValue', [keyToDate(k), null]);
    return;
  }
  emit('update:modelValue', keyNum(k) < keyNum(start)
    ? [keyToDate(k), keyToDate(start)]
    : [keyToDate(start), keyToDate(k)]);
};
</script>
